import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Globe, ArrowRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'lv', label: 'Latviešu', flag: '🇱🇻' },
  { code: 'lt', label: 'Lietuvių', flag: '🇱🇹' }
];

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { loginAsDemo, isAuthenticated } = useAuth();

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const handleEnterDemo = () => {
    loginAsDemo();
    navigate('/');
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Header */}
        <div className="login-header">
          <div className="login-logo">
            <Shield size={40} strokeWidth={1.5} />
          </div>
          <h1 className="login-title">Kliniktrack</h1>
          <p className="login-subtitle">{t('auth.welcomeMessage')}</p>
        </div>

        {/* Language Selector */}
        <div className="login-language">
          <Globe size={18} />
          <Select value={i18n.language} onValueChange={handleLanguageChange}>
            <SelectTrigger className="login-lang-select" data-testid="language-selector">
              <SelectValue>
                <span className="lang-display">
                  <span>{currentLang.flag}</span>
                  <span>{currentLang.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map(lang => (
                <SelectItem key={lang.code} value={lang.code}>
                  <span className="lang-display">
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Features */}
        <div className="login-features">
          <div className="feature-item">
            <span className="feature-check">✓</span>
            <span>{t('auth.feature1')}</span>
          </div>
          <div className="feature-item">
            <span className="feature-check">✓</span>
            <span>{t('auth.feature2')}</span>
          </div>
          <div className="feature-item">
            <span className="feature-check">✓</span>
            <span>{t('auth.feature3')}</span>
          </div>
        </div>

        {/* Enter Button */}
        <button
          onClick={handleEnterDemo}
          className="login-enter-btn"
          data-testid="btn-enter"
        >
          {t('auth.enterPlatform')}
          <ArrowRight size={20} />
        </button>

        {/* Footer */}
        <div className="login-footer">
          <p>{t('auth.secureNote')}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
